import React, { useEffect, useRef, useState } from 'react'

interface ChatInputProps {
  placeholder?: string
  /**
   * Animated placeholder text that appears as an overlay when input is empty.
   * Use with useTypewriterPlaceholder hook for typewriter effect.
   */
  animatedPlaceholder?: string
  value?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  disabled?: boolean
  className?: string
  multiline?: boolean
  autoFocus?: boolean
  /**
   * If true, the textarea auto-resizes up to a max height.
   * If false, the textarea stays fixed-height and scrolls internally when needed.
   */
  autoResize?: boolean
  /**
   * Maximum height available for the textarea (in px) when autoResize is enabled.
   * This should exclude any siblings (e.g. action rows) so we don't grow past the container.
   */
  maxHeightPx?: number
  onFocus?: () => void
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  /**
   * Optional base visual height (px) for the textarea.
   * Defaults to 72 (multiline) / 40 (single-line).
   */
  baseHeightPx?: number
}

export function ChatInput({
  placeholder = 'Ask a question...',
  animatedPlaceholder,
  value: controlledValue,
  onChange,
  onSubmit,
  disabled = false,
  className = '',
  multiline = true,
  autoFocus = true,
  autoResize = true,
  maxHeightPx = 320,
  onFocus,
  onBlur,
  onKeyDown,
  baseHeightPx
}: ChatInputProps) {
  const [internalValue, setInternalValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isControlled = controlledValue !== undefined
  const inputValue = isControlled ? controlledValue : internalValue

  // Default visual height: keep consistent across surfaces unless overridden.
  const effectiveBaseHeightPx = baseHeightPx ?? (multiline ? 72 : 40)

  useEffect(() => {
    // Don't auto-focus if user is currently searching
    if (textareaRef.current && !disabled && autoFocus) {
      textareaRef.current.focus()
    }
  }, [disabled, autoFocus])

  // Auto-resize textarea based on height limit
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current

      if (!autoResize) {
        textarea.style.height = `${effectiveBaseHeightPx}px`
        // Only show a scrollbar if content actually overflows the fixed height
        textarea.style.overflowY = textarea.scrollHeight > effectiveBaseHeightPx ? 'auto' : 'hidden'
        return
      }

      // Reset height to get accurate scrollHeight
      textarea.style.height = 'auto'

      const textareaMargin = multiline ? 0 : 8
      const maxTextareaHeight = Math.max(effectiveBaseHeightPx, maxHeightPx - textareaMargin)

      // Get the content height needed
      const scrollHeight = textarea.scrollHeight

      if (!multiline) {
        // For single-line mode: start at 14px and grow with content wraps
        const baseHeight = 14
        let height = Math.max(baseHeight, scrollHeight)
        if (height <= 28) {
          height = 14
        }
        const newHeight = Math.min(height, maxTextareaHeight)
        textarea.style.height = newHeight + 'px'
      } else {
        // For multiline mode: start at 24px minimum, grow with content
        const newHeight = Math.max(effectiveBaseHeightPx, Math.min(scrollHeight, maxTextareaHeight))
        textarea.style.height = newHeight + 'px'
      }

      // Ensure proper overflow behavior
      if (scrollHeight > maxTextareaHeight) {
        textarea.style.overflowY = 'auto'
      } else {
        textarea.style.overflowY = 'hidden'
      }
    }
  }, [inputValue, multiline, autoResize, effectiveBaseHeightPx, maxHeightPx])

  // Detect OS for appropriate command key
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value

    if (isControlled) {
      onChange?.(newValue)
    } else {
      setInternalValue(newValue)
    }
  }

  const handleSubmit = () => {
    if (inputValue.trim() && onSubmit) {
      onSubmit(inputValue.trim())

      // Clear input after submit if not controlled
      if (!isControlled) {
        setInternalValue('')
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) {
      return
    }

    if (event.key === 'Enter') {
      // Use OS-appropriate command key
      const isCommandKey = isMac ? event.metaKey : event.ctrlKey

      // Allow new line on Ctrl+Enter or Shift+Enter, otherwise submit
      if (isCommandKey || event.shiftKey) {
        // Let the default behavior happen (new line)
        return
      } else {
        // Submit on plain Enter
        event.preventDefault()
        handleSubmit()
      }
    }
  }

  const showAnimatedPlaceholder = animatedPlaceholder !== undefined && !inputValue

  return (
    <div className={`flex-1 min-h-0 flex relative ${className}`}>
      <textarea
        ref={textareaRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={showAnimatedPlaceholder ? '' : placeholder}
        disabled={disabled}
        className={
          multiline
            ? 'border-0 bg-transparent resize-none w-full focus:ring-0 focus:outline-none font-normal placeholder:text-tokens-neutral-text-quaternary text-tokens-neutral-text-primary z-20 relative transition-[height] duration-150 ease-out motion-reduce:transition-none'
            : 'border-0 bg-transparent resize-none w-full focus:ring-0 focus:outline-none font-medium z-20 relative placeholder:text-tokens-neutral-text-placeholder py-1 p-0 transition-[height] duration-150 ease-out motion-reduce:transition-none'
        }
        style={
          multiline
            ? {
                minHeight: `${effectiveBaseHeightPx}px`,
                height: `${effectiveBaseHeightPx}px`,
                lineHeight: '22px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px'
              }
            : {
                minHeight: `${effectiveBaseHeightPx}px`,
                lineHeight: '22px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                color: '#1A1A1A',
                fontFamily: 'Geist, sans-serif'
              }
        }
      />

      {showAnimatedPlaceholder && (
        <div
          className={
            multiline
              ? 'absolute inset-0 pointer-events-none z-10'
              : 'absolute left-0 right-0 top-[8px] px-3 pointer-events-none z-10 overflow-hidden h-[20px]'
          }
        >
          <span
            className={
              multiline
                ? 'block text-tokens-neutral-text-quaternary font-normal text-[14px] leading-[22px] whitespace-nowrap overflow-hidden text-ellipsis select-none'
                : 'text-tokens-neutral-text-quaternary font-medium text-[14px] leading-[22px] select-none'
            }
          >
            {animatedPlaceholder}
          </span>
        </div>
      )}
    </div>
  )
}
